/* eslint-disable no-undefined */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
/* eslint-disable complexity */
import React from "react";
import { Grid, Typography, Divider, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { trimStr } from "utils/string";
import { usePostgresViewer } from "components/viewer/context";
import SignSection from "./sign-section";
import type { CommonStepProps } from "../step-props";

type Props = Pick<
CommonStepProps,
| "formik"
| "onGoToStep"
| "onGoToSuccess"
| "annualCost"
| "monthlyBeforeDiscount"
| "annualPaymentDiscountAmount"
| "setupFeeOneTime"
| "preloadedTeam"
| "letterCosts"
| "signatureRequestId"
| "setSignatureRequestId"
| "submissionId"
| "setSubmissionId"
| "orgId"
>;

const Row: React.FC<{
  label: string;
  value: React.ReactNode;
  bold?: boolean;
}> = ({ label, value, bold }) => (
  <Grid container spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
    <Grid size={{ xs: 5 }}>
      <Typography variant="body2" color="textSecondary" sx={bold ? { fontWeight: "bold" } : undefined}>{label}</Typography>
    </Grid>
    <Grid size={{ xs: 7 }}>
      <Typography variant="body2" sx={bold ? { fontWeight: "bold" } : undefined}>{value}</Typography>
    </Grid>
  </Grid>
);

const Section: React.FC<{
  title: string;
  onEdit?: () => void;
  children: React.ReactNode;
}> = ({ title, onEdit, children }) => (
  <Grid size={{ xs: 12 }} sx={{ mb: 1 }}>
    <Grid container alignItems="center" wrap="nowrap" sx={{ mb: 1 }}>
      <Typography variant="subtitle2" sx={{ flex: 1 }}>{title}</Typography>
      {onEdit && (
        <IconButton size="small" color="primary" onClick={onEdit} aria-label={`Edit ${title}`}>
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Grid>
    {children}
  </Grid>
);

const formatCurrency = (value: number): string =>
  `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const StepReview: React.FC<Props> = ({
  formik,
  onGoToStep,
  onGoToSuccess,
  annualCost,
  monthlyBeforeDiscount,
  annualPaymentDiscountAmount,
  setupFeeOneTime,
  preloadedTeam,
  letterCosts,
  signatureRequestId,
  setSignatureRequestId,
  submissionId,
  setSubmissionId,
  orgId,
}) => {
  const viewer = usePostgresViewer();
  const values = formik.values || {};
  const billingCycle = trimStr(values.billingCycle);
  const isAnnual = billingCycle === "annually";
  const billingLabel = isAnnual ? "Annual" : "Quarterly";
  const showAnnualDiscount = isAnnual && annualPaymentDiscountAmount > 0;
  const annualCostBeforeDiscount = annualCost + annualPaymentDiscountAmount;
  const total = annualCost + setupFeeOneTime;

  const propertyName = trimStr(values.propertyName);
  const unitCount = values.unitCount === "" ? "" : Number(values.unitCount || 0);

  const pi = values.propertyInfo || {};
  const phone = trimStr(pi.phone);
  const ext = trimStr(pi.phoneExtension);
  const phoneDisplay = ext ? `${phone} x${ext}` : phone;
  const cityStateZip = [pi.city, pi.state, pi.zip]
    .map((x: string) => trimStr(x))
    .filter((x) => x !== "")
    .join(", ");
  const addressLines = (() => {
    const lines = [trimStr(pi.address1), trimStr(pi.address2), cityStateZip].filter((x) => x !== "");
    if (lines.length > 0) return lines;
    return pi.inDevelopment ? ["(No address required - Still in development)"] : [];
  })();
  const addressValue = addressLines.length > 0 ? addressLines.join(", ") : "(not set)";

  const signer = values.signer || {};
  const selfSigning = Boolean(signer.selfSigning) || signer.signerUserId === viewer?.id;
  const team = preloadedTeam || [];
  const signerUserId = trimStr(signer.signerUserId);
  const signerUser = team.find((user) => user.id === signerUserId);
  const signerName = selfSigning ? "You" : (trimStr(signerUser?.fullName) || (signerUserId ? `User ID: ${signerUserId}` : "Not selected"));
  const signerEmail = trimStr(signerUser?.email);

  const payment = values.payment || {};
  const methods = {
    // eslint-disable-next-line camelcase
    credit_card: "Credit Card",
    ach: "ACH",
    invoice: "Invoice",
  };
  const paymentLabel = methods[payment.method as keyof typeof methods] || "Invoice";

  return (
    <Grid size={{ xs: 12, md: 8, lg: 6 }}>
      <Grid container spacing={2}>
        <Section title="Plan & Pricing" onEdit={onGoToStep ? () => onGoToStep(0) : undefined}>
          <Row label="Billing Frequency" value={billingLabel} />
          {isAnnual ? (
            <>
              <Row
                label="Annual cost (before discount)"
                value={formatCurrency(annualCostBeforeDiscount)}
              />
              {showAnnualDiscount && (
                <Row
                  label="Annual payment discount"
                  value={`-${formatCurrency(annualPaymentDiscountAmount)}`}
                />
              )}
              <Row label="Set up fee" value={formatCurrency(setupFeeOneTime)} />
              <Row label="Total" value={formatCurrency(total)} bold />
            </>
          ) : (
            <>
              <Row label="Set up fee" value={formatCurrency(setupFeeOneTime)} />
              <Row
                label="Total monthly cost (before discount)"
                value={`${formatCurrency(monthlyBeforeDiscount)}/month`}
              />
              <Row label="Annual total" value={formatCurrency(annualCost)} />
            </>
          )}
          {letterCosts?.firstPageCost && (
            <Row label="Letter (first page)" value={`${formatCurrency(letterCosts.firstPageCost)}/letter`} />
          )}
          {letterCosts?.additionalPagesCost && (
            <Row label="Additional letter pages" value={`${formatCurrency(letterCosts.additionalPagesCost)}/page`} />
          )}
        </Section>

        <Divider style={{ width: "100%", margin: "8px 0" }} />

        <Section title="Property" onEdit={onGoToStep ? () => onGoToStep(2) : undefined}>
          <Row label="Property Name" value={propertyName || "(not set)"} />
          <Row label="Legal Name" value={trimStr(pi.legalName) || "(not set)"} />
          {typeof unitCount === "number" && (
            <Row label="Unit Count" value={unitCount.toLocaleString("en-US")} />
          )}
          <Row label="Phone" value={phoneDisplay || "(not set)"} />
          <Row label="Email" value={trimStr(pi.email) || "(not set)"} />
          <Row label="Address" value={addressValue} />
          <Row label="In Development" value={pi.inDevelopment ? "Yes" : "No"} />
        </Section>

        <Divider style={{ width: "100%", margin: "8px 0" }} />

        <Section title="Signer" onEdit={onGoToStep ? () => onGoToStep(3) : undefined}>
          <Row label="Self Signing" value={selfSigning ? "Yes" : "No"} />
          <Row label="Signer Name" value={signerName} />
          {!!signerEmail && <Row label="Signer Email" value={signerEmail} />}
          <Row label="Signer Title" value={trimStr(signer.title) || "(not set)"} />
        </Section>

        <Divider style={{ width: "100%", margin: "8px 0" }} />

        <Section title="Payment" onEdit={onGoToStep ? () => onGoToStep(4) : undefined}>
          <Row label="Method" value={paymentLabel} />
          <Row label="Accounting Email" value={trimStr(payment.accountingEmail) || "(not set)"} />
          {payment.method === "invoice" && (
            <Typography variant="caption" color="textSecondary">
              Accounting email will be used for invoicing.
            </Typography>
          )}
        </Section>

        <Divider style={{ width: "100%", margin: "8px 0" }} />

        <SignSection
          formik={formik}
          onGoToSuccess={onGoToSuccess}
          preloadedTeam={preloadedTeam}
          signatureRequestId={signatureRequestId}
          setSignatureRequestId={setSignatureRequestId}
          submissionId={submissionId}
          setSubmissionId={setSubmissionId}
          orgId={orgId}
        />
      </Grid>
    </Grid>
  );
};

export default StepReview;

